/**
 * ReminderRuleCard - Card individual de uma regra de lembrete
 * 
 * Exibe resumo da regra com toggle ativo/inativo,
 * offset humanizado, inbox e ações de editar/excluir.
 * 
 * @version 1.0.0
 * RELEASE: 2026-03-29 v1.0.0 - Criação do componente
 * RELEASE: 2026-10-02 v1.1.0 - Resumo da configuração de vistoria (status, referência, destinatários, repetição, tipo)
 */

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Bell, BellOff, Pencil, Trash2, Clock, MessageSquare, Inbox, List, ShieldAlert, CalendarDays } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import {
  INSPECTION_REMINDER_STATUSES,
  INSPECTION_RECIPIENT_MODES,
  INSPECTION_ANCHORS,
} from '@/components/reminders/InspectionReminderConfigFields';
import type { InspectionReminderConfig } from '@/hooks/useReminderRules';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { cn } from '@/lib/utils';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { ReminderQueueDialog } from './ReminderQueueDialog';
import type { ReminderRule } from '@/hooks/useReminderRules';

interface ReminderRuleCardProps {
  rule: ReminderRule;
  onEdit: (rule: ReminderRule) => void;
  onToggle: (ruleId: string, isActive: boolean) => void;
  onDelete: (ruleId: string) => void;
  isToggling?: boolean;
  isDeleting?: boolean;
  /** Permissão de edição (toggle + editar) — default: true */
  canEditRule?: boolean;
  /** Permissão de exclusão — default: true */
  canDeleteRule?: boolean;
}

/**
 * Converte offset_minutes em label humanizado
 * Negativos = antes, positivos = depois
 */
function humanizeOffset(minutes: number): string {
  const abs = Math.abs(minutes);
  const direction = minutes < 0 ? 'antes' : 'depois';

  if (abs < 60) return `${abs} min ${direction}`;
  if (abs < 1440) {
    const hours = Math.floor(abs / 60);
    return `${hours}h ${direction}`;
  }
  const days = Math.floor(abs / 1440);
  return `${days} dia${days > 1 ? 's' : ''} ${direction}`;
}

export function ReminderRuleCard({
  rule,
  onEdit,
  onToggle,
  onDelete,
  isToggling,
  isDeleting,
  canEditRule = true,
  canDeleteRule = true,
}: ReminderRuleCardProps) {
  const { currentTenant } = useAuth();
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showQueueDialog, setShowQueueDialog] = useState(false);
  const isBefore = rule.offset_minutes < 0;

  /**
   * Query para verificar se a regra possui itens vinculados na reminder_queue.
   * Usada para bloquear exclusão e preservar histórico de auditoria.
   */
  const { data: queueCount = 0 } = useQuery({
    queryKey: ['reminder-rule-queue-count', rule.id],
    queryFn: async () => {
      const { count, error } = await supabase
        .from('reminder_queue')
        .select('id', { count: 'exact', head: true })
        .eq('rule_id', rule.id)
        .eq('tenant_id', currentTenant?.id ?? '');
      if (error) return 0;
      return count ?? 0;
    },
    enabled: !!currentTenant?.id,
    staleTime: 30_000,
  });

  /** Indica se a regra tem disparos vinculados (bloqueio de exclusão) */
  const hasLinkedItems = queueCount > 0;

  return (
    <div
      className={cn(
        'group flex items-center gap-4 rounded-lg border p-3 transition-all',
        rule.is_active
          ? 'border-border/60 bg-card hover:border-primary/30 hover:shadow-sm'
          : 'border-border/30 bg-muted/20 opacity-60'
      )}
    >
      {/* Ícone de status */}
      <div className={cn(
        'flex items-center justify-center w-9 h-9 rounded-lg shrink-0',
        rule.is_active ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground'
      )}>
        {rule.is_active ? <Bell className="w-4 h-4" /> : <BellOff className="w-4 h-4" />}
      </div>

      {/* Informações da regra */}
      <div className="flex-1 min-w-0 space-y-1">
        <div className="flex items-center gap-2 flex-wrap">
          {/* Badge de offset */}
          <Badge
            variant={isBefore ? 'secondary' : 'destructive'}
            className="text-[10px] font-medium gap-1"
          >
            <Clock className="w-3 h-3" />
            {humanizeOffset(rule.offset_minutes)}
          </Badge>

          {/* Badge de inbox */}
          <Badge variant="outline" className="text-[10px] font-normal gap-1">
            <Inbox className="w-3 h-3" />
            {rule.inbox_name}
          </Badge>

          {/* Badge de tipo de mensagem */}
          <Badge variant="outline" className="text-[10px] font-normal gap-1">
            <MessageSquare className="w-3 h-3" />
            {rule.message_type === 'template' ? rule.template_name || 'Template' : 'Texto livre'}
          </Badge>

          {/* Escopo por agenda (apenas agendamento) — vazio significa todas */}
          {rule.entity_type === 'scheduling' && (
            rule.scope_labels && rule.scope_labels.length > 0 ? (
              rule.scope_labels.map((label) => (
                <Badge key={label} variant="outline" className="text-[10px] font-normal gap-1">
                  <CalendarDays className="w-3 h-3" />
                  {label}
                </Badge>
              ))
            ) : (
              <Badge variant="outline" className="text-[10px] font-normal gap-1 text-muted-foreground">
                <CalendarDays className="w-3 h-3" />
                Todas as agendas
              </Badge>
            )
          )}
          {/* v1.1.0: resumo da configuração de vistoria */}
          {rule.entity_type === 'vistoria' && (() => {
            const cfg = rule.config as InspectionReminderConfig | null | undefined;
            if (!cfg || !Array.isArray(cfg.statuses)) return null;
            const lbl = (list: readonly { value: string; label: string }[], v: string) =>
              list.find((x) => x.value === v)?.label ?? v;
            return (
              <>
                <Badge variant="outline" className="text-[10px] font-normal">
                  {cfg.statuses.map((s) => lbl(INSPECTION_REMINDER_STATUSES, s)).join(', ')}
                </Badge>
                <Badge variant="outline" className="text-[10px] font-normal">
                  {lbl(INSPECTION_ANCHORS, cfg.anchor)}
                </Badge>
                <Badge variant="outline" className="text-[10px] font-normal">
                  {cfg.recipient_modes.map((m) => lbl(INSPECTION_RECIPIENT_MODES, m)).join(', ')}
                </Badge>
                <Badge variant="outline" className="text-[10px] font-normal">
                  {cfg.repeat?.mode === 'interval'
                    ? `Repete a cada ${Math.round(cfg.repeat.every_minutes / 60)}h (máx. ${cfg.repeat.max_count})`
                    : 'Envia uma vez'}
                </Badge>
                <Badge variant="outline" className="text-[10px] font-normal text-muted-foreground">
                  {rule.scope_labels?.length ? rule.scope_labels.join(', ') : 'Todos os tipos'}
                </Badge>
              </>
            );
          })()}
        </div>

        {/* Preview da mensagem (truncado) */}
        {rule.message_template && (
          <p className="text-xs text-muted-foreground truncate max-w-md">
            {rule.message_template}
          </p>
        )}
      </div>

      {/* Ações */}
      <div className="flex items-center gap-2 shrink-0">
        {/* Toggle ativo/inativo — requer permissão de edição */}
        <Switch
          checked={rule.is_active}
          onCheckedChange={(checked) => onToggle(rule.id, checked)}
          disabled={isToggling || !canEditRule}
          className="scale-90"
        />

        {/* Botão ver disparos */}
        <Button
          variant="ghost"
          size="icon-sm"
          onClick={() => setShowQueueDialog(true)}
          className="transition-colors"
          title="Ver disparos"
        >
          <List className="w-3.5 h-3.5" />
        </Button>

        {/* Botão editar — requer permissão de edição */}
        {canEditRule && (
          <Button
            variant="ghost"
            size="icon-sm"
            onClick={() => onEdit(rule)}
            className="transition-colors"
          >
            <Pencil className="w-3.5 h-3.5" />
          </Button>
        )}

        {/* Botão excluir — bloqueado quando há disparos vinculados */}
        {canDeleteRule && (hasLinkedItems ? (
          /* Regra com itens vinculados: exibe tooltip informativo + dialog de orientação */
          <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <AlertDialogTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon-sm"
                      className="transition-colors text-muted-foreground cursor-not-allowed opacity-60"
                    >
                      <ShieldAlert className="w-3.5 h-3.5" />
                    </Button>
                  </AlertDialogTrigger>
                </TooltipTrigger>
                <TooltipContent side="top" className="text-xs max-w-[200px]">
                  Regra com {queueCount} disparo(s) vinculado(s) — desative ao invés de excluir
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle className="flex items-center gap-2">
                  <ShieldAlert className="w-5 h-5 text-destructive/70" />
                  Exclusão bloqueada
                </AlertDialogTitle>
                <AlertDialogDescription className="space-y-2">
                  <p>
                    Esta regra possui <strong>{queueCount} disparo(s) vinculado(s)</strong> no histórico.
                    Para preservar os registros de auditoria, a exclusão não é permitida.
                  </p>
                  <p>
                    Utilize o <strong>toggle de desativar</strong> para impedir novos disparos
                    sem perder o histórico existente.
                  </p>
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Entendi</AlertDialogCancel>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        ) : (
          /* Regra sem itens: exclusão normal com confirmação */
          <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
            <AlertDialogTrigger asChild>
              <Button
                variant="ghost"
                size="icon-sm"
                className="transition-colors text-destructive hover:text-destructive"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Excluir regra de lembrete?</AlertDialogTitle>
                <AlertDialogDescription>
                  Esta ação é irreversível. Lembretes pendentes desta regra serão cancelados.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancelar</AlertDialogCancel>
                <AlertDialogAction
                  onClick={() => onDelete(rule.id)}
                  disabled={isDeleting}
                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                >
                  Excluir
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        ))}
      </div>

      {/* Dialog de auditoria de disparos (lazy loading) */}
      <ReminderQueueDialog
        rule={rule}
        open={showQueueDialog}
        onOpenChange={setShowQueueDialog}
      />
    </div>
  );
}
